import api from '@/lib/api/axios'
import type { Object as ObjectEntity, ObjectVersion } from '@/types/entities'
import type { ListParams, PaginatedResponse } from '@/lib/api/types'

export const objectsApi = {
    list: (params?: ListParams & { limit?: number }) => {
        // Convert perPage to limit for backend
        const backendParams = params ? {
            ...params,
            limit: params.limit || params.perPage || 20,
            page: params.page || 1,
        } : { page: 1, limit: 20 }
        // Remove perPage if it exists since backend uses limit
        if (backendParams.perPage) {
            delete backendParams.perPage
        }
        return api.get<PaginatedResponse<ObjectEntity>>('/api/objects', { params: backendParams })
    },

    get: (id: string, scenario?: string) =>
        api.get<ObjectEntity>(`/api/objects/${id}`, { params: scenario ? { scenario } : {} }),

    create: (data: Omit<ObjectEntity, 'objectID'>) =>
        api.post<ObjectEntity>('/api/objects', data),

    update: (id: string, data: Partial<Omit<ObjectEntity, 'objectID'>>) =>
        api.put<ObjectEntity>(`/api/objects/${id}`, data),

    delete: (id: string) =>
        api.delete(`/api/objects/${id}`),

    getVersions: (id: string) =>
        api.get<ObjectVersion[]>(`/api/objects/${id}/versions`),

    getRolledUpRTO: (id: string, scenario?: string) =>
        api.get<Array<{ objectID: string; objectDescription: string; layerID: number | null; rto: number | null }>>(`/api/objects/${id}/rolled-up-rto`, { params: scenario ? { scenario } : {} }),

    getRolledUpRPO: (id: string, scenario?: string) =>
        api.get<Array<{ objectID: string; objectDescription: string; sourceLayer: number | null; rpo: number | null }>>(`/api/objects/${id}/rolled-up-rpo`, { params: scenario ? { scenario } : {} }),

    getRtoDetails: (id: string, scenario?: string) =>
        api.get<{ egressTime: number | null; linkTime: number | null; engineerRTO: number | null; endToEndTime: number | null }>(`/api/objects/${id}/rto-details`, { params: scenario ? { scenario } : {} }),

    getServiceTable: (id: string, scenario?: string) =>
        api.get<Array<{
            sourceID: string;
            description: string;
            sourceLayer: number | null;
            calculatedRPO: number | null;
            endToEndTime: number | null;
            metadata: string | null;
            vaultFiles: string | null;
            objectStatus: number | null;
            objectSize: number | null;
        }>>(`/api/objects/${id}/service-table`, { params: scenario ? { scenario } : {} }),

    getRunbook: (id: string, scenario?: string) =>
        api.get<Array<import('@/types/entities').RunbookTask>>(`/api/objects/${id}/runbook`, { params: scenario ? { scenario } : {} }),

    saveRunbook: (id: string, tasks: Array<import('@/types/entities').RunbookTask>, scenario?: string) =>
        api.post<{ success: boolean; taskCount: number }>(`/api/objects/${id}/runbook`, { tasks, scenario }),

    changeRunbookFromMaster: (id: string, masterRunbookId: number, scenario: string) =>
        api.post<{ success: boolean; taskCount: number }>(`/api/objects/${id}/runbook/change-from-master`, { masterRunbookId, scenario }),

    cloneRunbook: (id: string, sourceObjectID: string, sourceScenario: string, scenario: string) =>
        api.post<{ success: boolean; taskCount: number }>(`/api/objects/${id}/runbook/clone`, { sourceObjectID, sourceScenario, scenario }),

    saveRunbookAsMaster: (id: string, scenario: string, name: string, description: string) =>
        api.post<{ success: boolean; masterID: number; taskCount: number }>(`/api/objects/${id}/runbook/save-as-master`, { scenario, name, description }),

    getScenarios: (id: string) =>
        api.get<Array<{ id: number; name: string; isDefault: boolean }>>(`/api/objects/${id}/scenarios`),

    getAllScenarios: () =>
        api.get<Array<{ id: number; name: string; description: string; useCase: string; isDefault: boolean }>>('/api/objects/scenarios/all'),

    getScenario: (id: string, scenarioId: number) =>
        api.get<{ id: number; name: string; description: string; useCase: string }>(`/api/objects/${id}/scenarios/${scenarioId}`),

    getObjectsInformation: (id: string, scenario?: string) =>
        api.get<{ id: number | null; scenario: number | null; objectID: string | null; floorPlan: number | null; documentID: number | null; videoID: number | null; image: string | null }>(`/api/objects/${id}/information`, { params: scenario ? { scenario } : {} }),

    // Dropdown data endpoints
    checkObjectIDUnique: (objectID: string) =>
        api.get<{ isUnique: boolean }>(`/api/objects/check-unique/${encodeURIComponent(objectID)}`),

    getTeams: () =>
        api.get<Array<{ id: number; teamName: string }>>('/api/objects/teams'),

    getTechnologies: () =>
        api.get<Array<{ id: number; name: string; category: string }>>('/api/objects/technologies'),

    getBackupSolutions: () =>
        api.get<Array<{ id: number; name: string }>>('/api/objects/backup-solutions'),

    getMasterRunbooks: () =>
        api.get<Array<{ id: number; description: string }>>('/api/objects/master-runbooks'),

    getMasterRunbookInfo: (runbookId: number) =>
        api.get<{ engineerRTO: number | null; numberOfTasks: number | null; tasks: Array<any> }>(`/api/objects/master-runbooks/${runbookId}`),

    getStatsCount: () =>
        api.get<{ objects: number; services: number }>('/api/objects/stats/count'),

    getStatsByLocation: () =>
        api.get<{ objects: Array<{ Location: string; NoObjects: number }>; services: Array<{ Location: string; NoObjects: number }> }>('/api/objects/stats/by-location'),

    retrieveVersion: (data: { objectID: string; versionID: number | null; recoverRedObjects: string }) =>
        api.post<{ success: boolean; sqlState?: string; sqlMessage?: string; outcome?: string }>(`/api/objects/${data.objectID}/versions/${data.versionID}/retrieve`, {
            recoverRedObjects: data.recoverRedObjects,
        }),

    getStackRecoveryStats: (id: string, minSourceLayer?: number) => {
        const params = minSourceLayer !== undefined ? `?minSourceLayer=${minSourceLayer}` : '';
        return api.get<{ servicesCount: number; objectsCount: number; redObjectsCount: number; minSourceLayer: number; maxLayerID: number }>(`/api/objects/${id}/stack-recovery-stats${params}`);
    },

    getRedObjects: (id: string) =>
        api.get<Array<{
            redId: number | null;
            objectID: string;
            objectSecondary: string;
            redObjectName: string;
            redAnalytics: number | null;
            redDateTime: string | null;
            redSize: number | null;
            greenId: number | null;
            greenObjectName: string;
            greenAnalytics: number | null;
            greenDateTime: string | null;
            greenSize: number | null;
        }>>(`/api/objects/${id}/red-objects`),

    requestStackRecovery: (data: { objectID: string; recoverRedObjects: string; minSourceLayer: number; scenario: number }) =>
        api.post<{ success: boolean; sqlState?: string; sqlMessage?: string; outcome?: string }>(`/api/objects/${data.objectID}/stack-recovery-request`, {
            recoverRedObjects: data.recoverRedObjects,
            minSourceLayer: data.minSourceLayer,
            scenario: data.scenario,
        }),
    getAnomalySizeChart: (id: string) =>
        api.get<AnomalySizePoint[]>(`/api/objects/${id}/anomaly-size-chart`),

    getAnomalyHistory: (objectID: string) =>
        api.get<AnomalyHistoryPoint[]>(`/api/objects/${encodeURIComponent(objectID)}/anomaly-history`),

    importObjects: (file: File, objectService: string = 'Object') => {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('objectService', objectService)
        return api.post<ImportResult>('/api/objects/import', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    },

    importMapping: (file: File, scenarioId: number) => {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('scenarioId', String(scenarioId))
        return api.post<ImportResult>('/api/objects/import-mapping', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    },

    updateMarker: (objectID: string, floorPlanX: number | null, floorPlanY: number | null) =>
        api.patch<{ success: boolean; data: { objectID: string; floorPlanX: number | null; floorPlanY: number | null } }>(`/api/objects/${encodeURIComponent(objectID)}/marker`, { floorPlanX, floorPlanY }),

    // Documents
    getDocuments: (id: string, scenario?: string) =>
        api.get<ObjectDocument[]>(`/api/objects/${id}/documents`, { params: scenario ? { scenario } : {} }),

    addDocument: (id: string, data: { scenario: number; filename: string; originalName: string; description?: string; fileSize?: number; mimeType?: string }) =>
        api.post(`/api/objects/${id}/documents`, data),

    deleteDocument: (id: string, docId: number) =>
        api.delete(`/api/objects/${id}/documents/${docId}`),

    getDocumentLibrary: () =>
        api.get<ObjectDocument[]>('/api/objects/documents/library'),

    // Videos
    getVideos: (id: string, scenario?: string) =>
        api.get<ObjectVideo[]>(`/api/objects/${id}/videos`, { params: scenario ? { scenario } : {} }),

    addVideo: (id: string, data: { scenario: number; filename: string; originalName: string; description?: string; fileSize?: number; mimeType?: string; duration?: number }) =>
        api.post(`/api/objects/${id}/videos`, data),

    deleteVideo: (id: string, videoId: number) =>
        api.delete(`/api/objects/${id}/videos/${videoId}`),

    getVideoLibrary: () =>
        api.get<ObjectVideo[]>('/api/objects/videos/library'),
}

export interface ObjectDocument {
    id: number
    objectID?: string
    scenario?: number
    filename: string
    originalName: string
    description?: string | null
    fileSize?: number | null
    mimeType?: string | null
    uploadedAt?: string
    url: string
}

export interface ObjectVideo {
    id: number
    objectID?: string
    scenario?: number
    filename: string
    originalName: string
    description?: string | null
    fileSize?: number | null
    mimeType?: string | null
    duration?: number | null
    uploadedAt?: string
    url: string
    downloadUrl?: string
}

export interface AnomalyHistoryPoint {
    id: number
    objectID: string
    objectName: string
    datetime: string | null
    sizeMB: number | null
    noVaultFiles: number | null
    anomalyScore: number
    anomalyData: any
    objectSecondary: string
    transferTime: number | null
    analytics: number | null
}

export interface ImportResult {
    totalInFile: number
    imported: number
    alreadyExisting: number
    errors: Array<{ row: number; objectID?: string; error: string }>
}

export interface AnomalySizePoint {
    id: number | null
    objectName: string
    datetime: string
    size: number | null
    sizeMB: number | null
    anomaly: string | null
    backupType: string
    meanSize: number | null
    stdSize: number | null
    lowerBound: number | null
    upperBound: number | null
}
